import { useEffect, useState } from 'react';
import { Loader2, AlertTriangle, RotateCcw, Flag, FlagOff, Trash2, Mail, Calendar, Crown, Filter, X } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('masterToken') || localStorage.getItem('master_token') || localStorage.getItem('auth_token') || localStorage.getItem('token')}` });

const REASON_LABELS = {
  taking_a_break: 'Taking a break',
  found_other_platform: 'Found another platform',
  not_enough_business: 'Not enough business',
  technical_issues: 'Technical issues',
  personal_reasons: 'Personal reasons',
  master_admin_action: 'Removed by master admin',
  other: 'Other',
};
const RETURNING_LABELS = {
  yes_soon: 'Yes, soon (≤30d)',
  maybe_someday: 'Maybe someday',
  no_permanently: 'Leaving permanently',
};

const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleString(); } catch { return s; }
};

const Card = ({ children, className = '' }) => (
  <div className={`bg-[#2D2D52]/40 backdrop-blur-xl border border-white/5 shadow-[0_8px_32px_0_rgba(0,0,0,0.4)] rounded-2xl ${className}`}>{children}</div>
);

const StatCard = ({ label, value, color = 'text-white' }) => (
  <Card className="p-4">
    <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">{label}</p>
    <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
  </Card>
);

export default function MasterDeletedAccounts() {
  const [data, setData] = useState({ deleted: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [filters, setFilters] = useState({ role: '', returning: '', flagged: '' });

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.role) params.set('role', filters.role);
      if (filters.returning) params.set('returning', filters.returning);
      if (filters.flagged === 'true') params.set('flagged', 'true');
      if (filters.flagged === 'false') params.set('flagged', 'false');
      const res = await fetch(`${API}/master/deleted-accounts?${params}`, { headers: authHeaders() });
      if (res.ok) setData(await res.json());
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filters]);

  const flag = async (user_id, on) => {
    const res = await fetch(`${API}/master/deleted-accounts/${user_id}/${on ? 'flag' : 'unflag'}`, { method: 'POST', headers: authHeaders() });
    if (res.ok) {
      toast.success(on ? 'Flagged for fraud watchlist' : 'Unflagged');
      load();
      if (active?.user_id === user_id) setActive(null);
    } else { toast.error('Could not update flag'); }
  };

  const restore = async (user_id, email) => {
    if (!confirm(`Restore ${email}? Their profile + services will be re-created.`)) return;
    const res = await fetch(`${API}/master/deleted-accounts/${user_id}/restore`, { method: 'POST', headers: authHeaders() });
    const d = await res.json().catch(() => ({}));
    if (res.ok) { toast.success('Account restored'); load(); setActive(null); }
    else toast.error(d.detail || 'Could not restore');
  };

  const purge = async (user_id, email) => {
    if (!confirm(`PERMANENTLY delete the deleted-user record for ${email}? This cannot be undone.`)) return;
    const res = await fetch(`${API}/master/deleted-accounts/${user_id}`, { method: 'DELETE', headers: authHeaders() });
    if (res.ok) { toast.success('Permanently purged'); load(); setActive(null); }
    else toast.error('Could not purge');
  };

  return (
    <div className="space-y-5" data-testid="master-deleted-accounts">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-white font-syne">Deleted Accounts</h1>
        <p className="text-[#9CA3AF] text-sm mt-1">Audit closed accounts, restore mistakes, or flag suspicious deletes (auto-blocks the email from re-registering).</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total Deleted" value={data.stats?.total ?? 0} />
        <StatCard label="Vendors" value={data.stats?.vendors ?? 0} color="text-purple-300" />
        <StatCard label="Consumers" value={data.stats?.consumers ?? 0} color="text-cyan-300" />
        <StatCard label="Flagged" value={data.stats?.flagged ?? 0} color="text-red-400" />
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <Filter size={14} className="text-[#9CA3AF]" />
          <select value={filters.role} onChange={(e) => setFilters({ ...filters, role: e.target.value })} className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/15 text-white text-xs" data-testid="filter-role">
            <option value="" className="bg-[#13153A]">All roles</option>
            <option value="vendor" className="bg-[#13153A]">Vendors</option>
            <option value="consumer" className="bg-[#13153A]">Consumers</option>
          </select>
          <select value={filters.returning} onChange={(e) => setFilters({ ...filters, returning: e.target.value })} className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/15 text-white text-xs" data-testid="filter-returning">
            <option value="" className="bg-[#13153A]">Any returning answer</option>
            {Object.entries(RETURNING_LABELS).map(([v, l]) => <option key={v} value={v} className="bg-[#13153A]">{l}</option>)}
          </select>
          <select value={filters.flagged} onChange={(e) => setFilters({ ...filters, flagged: e.target.value })} className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/15 text-white text-xs" data-testid="filter-flagged">
            <option value="" className="bg-[#13153A]">All</option>
            <option value="true" className="bg-[#13153A]">Flagged only</option>
            <option value="false" className="bg-[#13153A]">Not flagged</option>
          </select>
        </div>
      </Card>

      {/* Table + Detail */}
      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-[#A78BFA]" /></div>
      ) : data.deleted.length === 0 ? (
        <div className="text-center py-16 rounded-2xl border border-dashed border-white/10">
          <p className="text-white font-semibold">No deleted accounts</p>
          <p className="text-[#9CA3AF] text-sm">When users delete their accounts, they'll appear here for review.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* List */}
          <Card className="p-2 lg:col-span-2 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-white/5 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left text-[#9CA3AF] font-semibold">Email · Name</th>
                    <th className="px-3 py-2 text-left text-[#9CA3AF] font-semibold">Role</th>
                    <th className="px-3 py-2 text-left text-[#9CA3AF] font-semibold">Reason</th>
                    <th className="px-3 py-2 text-left text-[#9CA3AF] font-semibold">Returning</th>
                    <th className="px-3 py-2 text-left text-[#9CA3AF] font-semibold">Deleted</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.deleted.map((u) => (
                    <tr key={u.user_id} onClick={() => setActive(u)} className={`cursor-pointer border-t border-white/5 hover:bg-white/5 ${active?.user_id === u.user_id ? 'bg-purple-500/10' : ''}`} data-testid={`deleted-row-${u.user_id}`}>
                      <td className="px-3 py-2.5">
                        <div className="text-white font-medium truncate max-w-[200px]">{u.name || '—'}</div>
                        <div className="text-[#9CA3AF] truncate max-w-[200px]">{u.email}</div>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="px-1.5 py-0.5 rounded text-[10px] bg-white/5 text-zinc-300">{u.role || '—'}</span>
                      </td>
                      <td className="px-3 py-2.5 text-zinc-300">{REASON_LABELS[u.exit_survey?.reason] || '—'}</td>
                      <td className="px-3 py-2.5 text-zinc-300">{RETURNING_LABELS[u.exit_survey?.returning] || '—'}</td>
                      <td className="px-3 py-2.5 text-[#9CA3AF] whitespace-nowrap">{fmtDate(u.deleted_at)}</td>
                      <td className="px-3 py-2.5">
                        {u.is_flagged && <span className="px-1.5 py-0.5 rounded text-[10px] bg-red-500/15 text-red-400 flex items-center gap-1 w-fit"><Flag size={9} /> Flagged</span>}
                        {u.is_restored && <span className="px-1.5 py-0.5 rounded text-[10px] bg-green-500/15 text-green-400 flex items-center gap-1 w-fit"><RotateCcw size={9} /> Restored</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Detail */}
          <Card className="p-5 lg:sticky lg:top-4 lg:self-start max-h-[calc(100vh-160px)] overflow-y-auto">
            <h3 className="font-syne text-white font-bold mb-3">Account Details</h3>
            {!active ? (
              <p className="text-sm text-[#9CA3AF]">Click a row to see the full exit survey + profile snapshot.</p>
            ) : (
              <div className="space-y-3 text-sm" data-testid="deleted-detail">
                <div className="flex items-center gap-2">
                  <span className="text-white font-bold">{active.name || '—'}</span>
                  {active.is_flagged && <span className="px-1.5 py-0.5 rounded text-[10px] bg-red-500/15 text-red-400 flex items-center gap-1"><Flag size={9} /> Flagged</span>}
                </div>
                <div className="text-[#9CA3AF] flex items-center gap-1.5"><Mail size={11} />{active.email}</div>
                <div className="text-[#9CA3AF] flex items-center gap-1.5"><Calendar size={11} />Deleted {fmtDate(active.deleted_at)}</div>
                {active.snapshot?.vendor?.business_name && (
                  <div className="text-[#9CA3AF] flex items-center gap-1.5"><Crown size={11} />{active.snapshot.vendor.business_name}</div>
                )}

                <hr className="border-white/10" />

                <div>
                  <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider mb-1">Reason for leaving</p>
                  <p className="text-white">{REASON_LABELS[active.exit_survey?.reason] || '—'}</p>
                </div>
                <div>
                  <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider mb-1">Plans to return</p>
                  <p className="text-white">{RETURNING_LABELS[active.exit_survey?.returning] || '—'}</p>
                </div>
                {active.exit_survey?.feedback && (
                  <div>
                    <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider mb-1">Feedback</p>
                    <p className="text-white whitespace-pre-wrap p-3 rounded-lg bg-white/5">{active.exit_survey.feedback}</p>
                  </div>
                )}

                {active.snapshot?.services?.length > 0 && (
                  <div>
                    <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider mb-1">Services they had ({active.snapshot.services.length})</p>
                    <ul className="text-zinc-300 text-xs list-disc list-inside space-y-0.5">
                      {active.snapshot.services.slice(0, 5).map((s) => <li key={s.service_id || s.title} className="truncate">{s.title}</li>)}
                      {active.snapshot.services.length > 5 && <li className="text-[#9CA3AF]">+{active.snapshot.services.length - 5} more</li>}
                    </ul>
                  </div>
                )}

                <hr className="border-white/10" />

                <div className="grid grid-cols-2 gap-2">
                  {active.is_flagged ? (
                    <button onClick={() => flag(active.user_id, false)} className="px-3 py-2 rounded-lg bg-zinc-500/15 hover:bg-zinc-500/25 text-zinc-200 text-xs font-semibold flex items-center justify-center gap-1" data-testid="unflag-btn"><FlagOff size={11} /> Unflag</button>
                  ) : (
                    <button onClick={() => flag(active.user_id, true)} className="px-3 py-2 rounded-lg bg-red-500/15 hover:bg-red-500/25 text-red-300 text-xs font-semibold flex items-center justify-center gap-1" data-testid="flag-btn"><Flag size={11} /> Flag for fraud</button>
                  )}
                  <button onClick={() => restore(active.user_id, active.email)} disabled={active.is_restored} className="px-3 py-2 rounded-lg bg-green-500/15 hover:bg-green-500/25 text-green-300 text-xs font-semibold flex items-center justify-center gap-1 disabled:opacity-50" data-testid="restore-btn"><RotateCcw size={11} /> {active.is_restored ? 'Restored' : 'Restore'}</button>
                  <button onClick={() => purge(active.user_id, active.email)} className="col-span-2 px-3 py-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-300 text-xs font-semibold flex items-center justify-center gap-1" data-testid="purge-btn"><Trash2 size={11} /> Purge permanently</button>
                </div>

                {active.is_flagged && (
                  <div className="rounded-lg bg-red-500/10 border border-red-500/30 px-3 py-2 text-red-200 text-xs flex items-start gap-2">
                    <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
                    <span>This email is blocked from re-registering. Visitors trying to sign up with it get a "contact support" message.</span>
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
